import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { submitQuiz, fetchBrushes, api } from "../lib/api";
import SEO from "../components/SEO";
import ShareResultBlock from "../components/ShareResultBlock";

const POWDER = "https://static.prod-images.emergentagent.com/jobs/b676cfa9-b99a-467a-b748-e01e0fba16e9/images/5c97f9724bfdaad482043e8f51261955fa7fece6ffe9ee3b63347e7b3c7de85d.png";

const STEPS = [
  {
    key: "look",
    title: "What look are you going for?",
    multi: false,
    options: [
      { id: "natural", label: "Natural & glowy" },
      { id: "glam", label: "Sculpted glam" },
      { id: "smoky", label: "Smoky & smoldering" },
      { id: "minimal", label: "Minimal everyday" },
    ],
  },
  {
    key: "focus",
    title: "Where do you want to focus?",
    multi: true,
    options: [
      { id: "face", label: "Face / Base" },
      { id: "cheeks", label: "Cheeks" },
      { id: "eyes", label: "Eyes" },
      { id: "lips", label: "Lips" },
    ],
  },
  {
    key: "skill",
    title: "How experienced are you?",
    multi: false,
    options: [
      { id: "beginner", label: "Just starting out" },
      { id: "intermediate", label: "Comfortable with basics" },
      { id: "advanced", label: "Confident artist" },
    ],
  },
  {
    key: "finish",
    title: "What finish do you prefer?",
    multi: false,
    options: [
      { id: "matte", label: "Soft matte" },
      { id: "balanced", label: "Balanced satin" },
      { id: "dewy", label: "Dewy & luminous" },
    ],
  },
  {
    key: "coverage",
    title: "How much coverage do you want?",
    multi: false,
    options: [
      { id: "light", label: "Light tint" },
      { id: "medium", label: "Medium coverage" },
      { id: "full", label: "Full coverage" },
    ],
  },
];

export default function Quiz() {
  const [searchParams] = useSearchParams();
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({
    look: "",
    focus: [],
    skill: "",
    finish: "",
    coverage: "",
  });
  const [result, setResult] = useState(null);
  const [brushMap, setBrushMap] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Personalized PDF capture state
  const [pdfEmail, setPdfEmail] = useState("");
  const [pdfName, setPdfName] = useState("");
  const [pdfError, setPdfError] = useState(null);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [pdfSubmitting, setPdfSubmitting] = useState(false);

  const downloadPdf = async (overrideEmail, overrideName) => {
    setPdfError(null);
    setPdfSubmitting(true);
    try {
      const res = await api.post(
        "/quiz/pdf",
        {
          answers,
          email: (overrideEmail || pdfEmail).trim(),
          name: (overrideName || pdfName).trim(),
        },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "your-brush-edit.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail
        || (e?.response?.data instanceof Blob ? "Couldn't generate the PDF. Try again." : "Couldn't generate the PDF. Try again.");
      setPdfError(msg);
      return false;
    } finally {
      setPdfSubmitting(false);
    }
  };

  const submitPdf = async (e) => {
    e.preventDefault();
    if (!pdfEmail.trim()) {
      setPdfError("Email is required");
      return;
    }
    const ok = await downloadPdf();
    if (ok) setPdfSuccess(true);
  };

  // If arriving via a shared result link (?shared=brush-id1,brush-id2,…),
  // skip the quiz steps and jump straight to a read-only result view.
  useEffect(() => {
    const shared = searchParams.get("shared");
    if (!shared) return;
    const ids = shared.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 6);
    if (!ids.length) return;
    (async () => {
      const all = await fetchBrushes("all");
      const map = {};
      all.brushes.forEach((b) => (map[b.id] = b));
      setBrushMap(map);
      setResult({
        recommended_ids: ids.filter((id) => map[id]),
        summary: "A friend shared their BrushBasics edit with you. These are the brushes they were matched with — take the quiz yourself to find your own.",
      });
    })().catch(() => {});
  }, []);

  const step = STEPS[stepIdx];
  const totalSteps = STEPS.length;

  const select = (val) => {
    if (step.multi) {
      const arr = answers[step.key];
      const next = arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
      setAnswers({ ...answers, [step.key]: next });
    } else {
      setAnswers({ ...answers, [step.key]: val });
    }
  };

  const canAdvance = step.multi
    ? answers[step.key].length > 0
    : !!answers[step.key];

  const next = async () => {
    if (stepIdx < totalSteps - 1) {
      setStepIdx(stepIdx + 1);
    } else {
      setSubmitting(true);
      try {
        const [res, all] = await Promise.all([submitQuiz(answers), fetchBrushes("all")]);
        const map = {};
        all.brushes.forEach((b) => (map[b.id] = b));
        setBrushMap(map);
        setResult(res);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const back = () => stepIdx > 0 && setStepIdx(stepIdx - 1);

  const reset = () => {
    setStepIdx(0);
    setAnswers({ look: "", focus: [], skill: "", finish: "", coverage: "" });
    setResult(null);
    setPdfEmail("");
    setPdfName("");
    setPdfError(null);
    setPdfSuccess(false);
  };

  if (result) {
    return (
      <div
        data-testid="quiz-result"
        className="relative min-h-[80vh]"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(249,247,243,0.92), rgba(249,247,243,0.98)), url(${POWDER})`,
          backgroundSize: "cover",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="overline mb-4">Your edit</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-light text-stone-900 leading-tight">
            The brushes built for you.
          </h1>
          <p className="mt-5 max-w-2xl text-base text-stone-600 leading-relaxed">{result.summary}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12" data-testid="quiz-recommendations">
            {result.recommended_ids.map((id, i) => {
              const b = brushMap[id];
              if (!b) return null;
              return (
                <Link
                  to={`/brush/${b.id}`}
                  key={b.id}
                  data-testid={`rec-${b.id}`}
                  className="bg-white border border-[#E5E0D8] bb-card-hover"
                >
                  <div className="aspect-[4/5] bg-[#F2EFEB] overflow-hidden">
                    <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <div className="overline mb-2">{(i + 1).toString().padStart(2, "0")} · {b.category}</div>
                    <div className="font-serif-display text-xl text-stone-900">{b.name}</div>
                    <p className="text-sm text-stone-500 italic mt-1">{b.tagline}</p>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Personalized PDF capture */}
          <div
            data-testid="quiz-pdf-section"
            className="mt-16 lg:mt-20 bg-[#2C2A29] text-stone-100 p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center"
          >
            <div>
              <div className="overline mb-3" style={{ color: "#E8D5D1" }}>1-Page Printable</div>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-light leading-tight">
                Take this edit with you.
                <br />
                <em className="italic">Print it. Pin it to your mirror.</em>
              </h2>
              <p className="mt-4 text-sm text-stone-300 leading-relaxed max-w-md">
                We&apos;ll send you a personalized one-page PDF with these {result.recommended_ids.length} brushes, your look, and the technique to start with.
              </p>
            </div>
            {pdfSuccess ? (
              <div data-testid="quiz-pdf-success" className="bg-[#F9F7F3] text-stone-900 p-6">
                <div className="overline mb-2">Ready</div>
                <h3 className="font-serif-display text-2xl text-stone-900 leading-tight">Your edit is downloading.</h3>
                <p className="mt-2 text-sm text-stone-600">If the download didn&apos;t start, tap below.</p>
                <button
                  data-testid="quiz-pdf-redownload"
                  onClick={() => downloadPdf()}
                  className="mt-5 text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-6 py-3 transition-colors"
                >
                  Download Again
                </button>
              </div>
            ) : (
              <form data-testid="quiz-pdf-form" onSubmit={submitPdf} className="bg-[#F9F7F3] text-stone-900 p-6">
                <label className="overline block mb-2" htmlFor="qp-email">Email</label>
                <input
                  id="qp-email"
                  data-testid="quiz-pdf-email"
                  type="email"
                  required
                  value={pdfEmail}
                  onChange={(e) => setPdfEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full text-sm bg-white border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3"
                />
                <label className="overline block mb-2 mt-4" htmlFor="qp-name">Name (optional)</label>
                <input
                  id="qp-name"
                  data-testid="quiz-pdf-name"
                  type="text"
                  value={pdfName}
                  onChange={(e) => setPdfName(e.target.value)}
                  placeholder="Mia"
                  className="w-full text-sm bg-white border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3"
                />
                {pdfError && <p data-testid="quiz-pdf-error" className="mt-3 text-sm text-red-700">{pdfError}</p>}
                <button
                  data-testid="quiz-pdf-submit"
                  type="submit"
                  disabled={pdfSubmitting || !pdfEmail.trim()}
                  className="mt-5 w-full text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] disabled:bg-stone-300 text-white px-7 py-4 transition-colors"
                >
                  {pdfSubmitting ? "Preparing your edit…" : "Send My 1-Page Edit"}
                </button>
                <p className="mt-4 text-xs text-stone-500 leading-relaxed">
                  By submitting, you agree to receive occasional emails from BrushBasics. Unsubscribe anytime.
                </p>
              </form>
            )}
          </div>

          <div className="mt-12 flex flex-wrap gap-4">
            <button
              data-testid="quiz-retake"
              onClick={reset}
              className="text-xs uppercase tracking-[0.22em] border border-[#8C6A5D] text-[#8C6A5D] hover:bg-[#F2EFEB] px-6 py-3 transition-colors"
            >
              Retake the Quiz
            </button>
            <Link
              to="/catalog"
              className="text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] text-white hover:bg-[#70544A] px-6 py-3 transition-colors"
            >
              Browse Full Catalog
            </Link>
          </div>

          <ShareResultBlock result={result} brushMap={brushMap} />
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="quiz-page"
      className="relative min-h-[80vh]"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(249,247,243,0.88), rgba(249,247,243,0.98)), url(${POWDER})`,
        backgroundSize: "cover",
        backgroundAttachment: "fixed",
      }}
    >
      <SEO
        title="Brush Finder Quiz — Find Your Perfect Makeup Brushes"
        description="A 60-second quiz that recommends the right makeup brushes for your routine, skill level, and signature look. Free, personalized, and emailed to you as a PDF."
        path="/quiz"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="overline mb-4">Brush Finder · Step {stepIdx + 1} of {totalSteps}</div>

        <div className="h-px bg-[#E5E0D8] w-full mb-10 relative overflow-hidden">
          <div
            className="absolute left-0 top-0 h-full bg-[#8C6A5D] transition-all duration-500"
            style={{ width: `${((stepIdx + 1) / totalSteps) * 100}%` }}
          />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step.key}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
          >
            <h1 className="font-serif-display text-4xl sm:text-5xl font-light text-stone-900 leading-[1.1] mb-10">
              {step.title}
            </h1>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid={`quiz-step-${step.key}`}>
              {step.options.map((opt) => {
                const selected = step.multi
                  ? answers[step.key].includes(opt.id)
                  : answers[step.key] === opt.id;
                return (
                  <button
                    key={opt.id}
                    data-testid={`quiz-option-${step.key}-${opt.id}`}
                    onClick={() => select(opt.id)}
                    className={`text-left px-6 py-5 border transition-all duration-300 ${
                      selected
                        ? "bg-[#2C2A29] text-white border-[#2C2A29]"
                        : "bg-white border-[#E5E0D8] text-stone-700 hover:border-[#8C6A5D]"
                    }`}
                  >
                    <div className="font-serif-display text-2xl leading-tight">{opt.label}</div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-12 flex justify-between items-center">
          <button
            data-testid="quiz-back"
            onClick={back}
            disabled={stepIdx === 0}
            className="text-xs uppercase tracking-[0.22em] text-stone-500 hover:text-stone-900 disabled:opacity-30"
          >
            ← Back
          </button>
          <button
            data-testid="quiz-next"
            onClick={next}
            disabled={!canAdvance || submitting}
            className="text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] disabled:bg-stone-300 text-white px-7 py-4 transition-colors"
          >
            {stepIdx === totalSteps - 1 ? (submitting ? "Curating…" : "See My Brushes") : "Next →"}
          </button>
        </div>
      </div>
    </div>
  );
}
